package team.platforma.multiplatform_material_extension.calendar.calendar_regular.core_ui

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.AnimationEndReason
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerInputChange
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.todayIn
import team.platforma.multiplatform_material_extension.calendar.core.core_ui.CalendarBadge
import team.platforma.multiplatform_material_extension.calendar.style.CalendarViewStyle
import team.platforma.multiplatform_material_extension.calendar.utils.AnimatedMonthChange
import team.platforma.multiplatform_material_extension.calendar.utils.AnimatedMonthChangeListener
import team.platforma.multiplatform_material_extension.calendar.utils.CalendarBadges
import team.platforma.multiplatform_material_extension.calendar.utils.CalendarBadgesListener
import team.platforma.multiplatform_material_extension.calendar.utils.CalendarRange
import team.platforma.multiplatform_material_extension.calendar.utils.daysInMonth
import team.platforma.multiplatform_material_extension.util.gestures.HorizontalGestures
import team.platforma.multiplatform_material_extension.util.gestures.HorizontalGesturesListener
import kotlin.math.abs

@Composable
internal fun CalendarRegularDatesView(
    selectedDate: LocalDate,
    year: Int,
    month: Int,
    onDateSelected: (LocalDate) -> Unit,
    calendarRange: CalendarRange,
    onPreviousMonth: () -> Unit,
    onNextMonth: () -> Unit,
    adaptiveHeight: Boolean,
    calendarBadges: CalendarBadges?,
    horizontalGestures: HorizontalGestures?,
    animatedMonthChange: AnimatedMonthChange?,
    animateBadge: Boolean
) {

    val daysInMonth = daysInMonth(year, month)
    val firstDayOfWeek = LocalDate(year, month, 1).dayOfWeek.isoDayNumber

    var rows = 6

    if (adaptiveHeight) {
        val totalCells = (daysInMonth + firstDayOfWeek - 1)
        rows = if (totalCells % 7 == 0) {
            totalCells / 7
        } else {
            val lastRowStartIndex = totalCells - (totalCells % 7)
            val hasDaysInLastRow = (lastRowStartIndex until totalCells).any { index ->
                val dayOfMonth = index - firstDayOfWeek + 1
                dayOfMonth in 1..daysInMonth
            }
            totalCells / 7 + if (hasDaysInLastRow) 1 else 0
        }
    }


    var date by remember { mutableStateOf(selectedDate) }

    var dragOffset by remember { mutableStateOf(Animatable(0f)) }
    var reversAnimation by remember { mutableStateOf(false) }

    val coroutineScope = rememberCoroutineScope()

    DisposableEffect(horizontalGestures) {

        val listener = object : HorizontalGesturesListener {

            var width = 1f
            var offset = 0f
            var endDrag = false

            override fun width(float: Float) {
                width = float
            }

            override fun onDragStart(offset: Offset) {
                reversAnimation = false
            }

            override fun onHorizontalDrag(change: PointerInputChange, dragAmount: Float) {

                if (!endDrag) {
                    offset += dragAmount
                    dragOffset = Animatable(((abs(offset)) / width).coerceIn(0f, 1f))

                    val threshold = width / 2
                    if (offset > threshold) {
                        onPreviousMonth()
                        endDrag = true
                        offset = 0f
                    } else if (offset < -threshold) {
                        onNextMonth()
                        endDrag = true
                        offset = 0f
                    }
                }
            }

            override fun onDragCancel() {
                offset = 0f
                endDrag = false
                reversAnimation = true
            }

            override fun onDragEnd() {
                offset = 0f
                endDrag = false
                reversAnimation = true
            }
        }

        horizontalGestures?.addListener(listener)

        onDispose {
            horizontalGestures?.removeListener(listener)
        }
    }

    if (reversAnimation) {
        coroutineScope.launch {
            dragOffset.animateTo(
                targetValue = 0f,
                animationSpec = tween(durationMillis = 250)
            )
        }
    }

    DisposableEffect(animatedMonthChange) {
        val listener = object : AnimatedMonthChangeListener {
            override fun fadeout(animationEnd: () -> Unit) {
                coroutineScope.launch {
                    val animation = dragOffset.animateTo(
                        targetValue = 0.5f,
                        animationSpec = tween(durationMillis = 100)
                    )
                    when (animation.endReason) {
                        AnimationEndReason.BoundReached -> {}
                        AnimationEndReason.Finished -> {
                            animationEnd.invoke()
                            reversAnimation = true
                        }
                    }
                }
            }


        }
        animatedMonthChange?.addListener(listener)
        onDispose {
            animatedMonthChange?.removeListener(listener)
        }
    }


    Column {

        Spacer(modifier = Modifier.height(8.dp))


        Box(
            modifier = Modifier.graphicsLayer {
                scaleX = 1f - 0.2f * dragOffset.value
                scaleY = 1f - 0.2f * dragOffset.value
                alpha = 1f - 1f * (2f * dragOffset.value)
            }

        ) {
            CalendarRegularContent(
                rows,
                firstDayOfWeek,
                daysInMonth,
                year,
                month,
                calendarRange,
                date,
                onDateSelected = {
                    date = it
                    onDateSelected(date)
                },
                calendarBadges = calendarBadges,
                coroutineScope = coroutineScope,
                animateBadge = animateBadge
            )
        }

    }
}

@Composable
private fun CalendarRegularContent(
    rows: Int,
    firstDayOfWeek: Int,
    daysInMonth: Int,
    year: Int,
    month: Int,
    calendarRange: CalendarRange,
    date: LocalDate,
    onDateSelected: (LocalDate) -> Unit,
    calendarBadges: CalendarBadges?,
    coroutineScope: CoroutineScope,
    animateBadge: Boolean
) {

    var badgesMap by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }

    val dateNow: LocalDate = Clock.System.todayIn(TimeZone.currentSystemDefault())

    DisposableEffect(calendarBadges) {
        val listener = object : CalendarBadgesListener {
            override fun onSetBadgeMap(map: Map<String, Int>) {
                badgesMap = badgesMap + map
            }

        }
        calendarBadges?.addListener(listener)

        onDispose {
            calendarBadges?.removeListener(listener)
        }
    }

    var maxWidth by remember { mutableStateOf(0f) }
    var boxWidth by remember { mutableStateOf(0f) }
    val density = LocalDensity.current

    Column(
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.fillMaxWidth().onGloballyPositioned { layoutCoordinates ->
            maxWidth = layoutCoordinates.size.width.toFloat()
        }
    ) {
        val cellSize = with(density) { maxWidth.toDp() / 7 }

        for (row in 0 until rows) {
            Row(
                modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center
            ) {

                for (col in 0..6) {
                    val dayIndex = row * 7 + col
                    val dayOfMonth = dayIndex - firstDayOfWeek + 2

                    Box(
                        modifier = Modifier.size(cellSize),
                        contentAlignment = Alignment.Center
                    ) {
                        if (dayOfMonth in 1..daysInMonth) {

                            val currentDate = LocalDate(year, month, dayOfMonth)

                            val enabled =
                                currentDate in calendarRange.max..calendarRange.min

                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {

                                Box(
                                    modifier = Modifier.fillMaxSize()
                                        .padding(
                                            horizontal = CalendarViewStyle.dateStyle().cellPadding.horizontal,
                                            vertical = CalendarViewStyle.dateStyle().cellPadding.vertical
                                        )
                                        .border(
                                            width = CalendarViewStyle.dateStyle().borderWidth,
                                            color = if (currentDate == dateNow) CalendarViewStyle.dateStyle().currentDateBorder else CalendarViewStyle.dateStyle().defaultDateBorder,
                                            shape = CalendarViewStyle.dateStyle().shape
                                        )
                                )

                                Box(modifier = Modifier.fillMaxSize()
                                    .padding(
                                        horizontal = CalendarViewStyle.dateStyle().cellPadding.horizontal,
                                        vertical = CalendarViewStyle.dateStyle().cellPadding.vertical
                                    )
                                    .background(
                                        color = if (currentDate == date) CalendarViewStyle.dateStyle().checkedDateBackground else CalendarViewStyle.dateStyle().defaultDateBackground,
                                        shape = CalendarViewStyle.dateStyle().shape
                                    ).clip(CalendarViewStyle.dateStyle().shape).border(
                                        width = CalendarViewStyle.dateStyle().borderWidth,
                                        color = if (currentDate == date) CalendarViewStyle.dateStyle().checkedDateBorder else CalendarViewStyle.dateStyle().defaultDateBorder,
                                        shape = CalendarViewStyle.dateStyle().shape
                                    ).clickable(
                                        interactionSource = remember { MutableInteractionSource() },
                                        indication = null,
                                        enabled = enabled
                                    ) {
                                        onDateSelected(currentDate)
                                    }
                                ) {
                                    Box(
                                        Modifier.fillMaxSize()
                                            .onGloballyPositioned { layoutCoordinates ->
                                                boxWidth = layoutCoordinates.size.width.toFloat()
                                            }
                                    )
                                }

                                Text(
                                    text = "$dayOfMonth", color = if (enabled) {
                                        if (currentDate == date) {
                                            CalendarViewStyle.dateStyle().checkedDateText
                                        } else {
                                            CalendarViewStyle.dateStyle().enabledDateText
                                        }
                                    } else {
                                        CalendarViewStyle.dateStyle().disableDateText
                                    }, style = TextStyle(
                                        fontSize = (cellSize.value * CalendarViewStyle.dateStyle().fontSize).sp,
                                        fontFamily = CalendarViewStyle.dateStyle().fontFamily,
                                        fontWeight = CalendarViewStyle.dateStyle().fontWeight,
                                        textAlign = TextAlign.Center,
                                        lineHeight = TextUnit.Unspecified,
                                        letterSpacing = 0.sp
                                    ), maxLines = 1
                                )

                                CalendarBadge(
                                    value = badgesMap[currentDate.toString()] ?: 0,
                                    cellSize = with(density) { boxWidth.toDp() }
                                )

                            }

                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun showBadge(
    itemHeight: Float,
    value: Int,
    animate: Boolean,
    coroutineScope: CoroutineScope
) {

    val animateItemHeight by remember { mutableStateOf(Animatable(if (animate) 0f else itemHeight)) }

    if (animate) {
        coroutineScope.launch {
            animateItemHeight.animateTo(
                targetValue = itemHeight,
                animationSpec = tween(durationMillis = 50)
            )
        }
    }

    Box(
        modifier = Modifier.fillMaxSize()
            .padding((itemHeight * 0.01).dp),
        contentAlignment = Alignment.TopEnd
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .width((animateItemHeight.value * 0.14).dp)
                    .height((animateItemHeight.value * 0.14).dp)
//                    .background(
//                        color = calendarBadgeStyle().badgeBackgroundColor,
//                        shape = calendarBadgeStyle().badgeShape
//                    )
                ,
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = if (value >= 100) "99" else value.toString(),
//                    color = calendarBadgeStyle().badgeTextColor,
                    style = TextStyle(
                        fontSize = (animateItemHeight.value * 0.09).sp,
//                        fontWeight = calendarBadgeStyle().badgeTextFontWeight,
                        textAlign = TextAlign.Center,
                        lineHeight = TextUnit.Unspecified,
                        letterSpacing = 0.sp
                    ),
                    maxLines = 1
                )
            }
            if (value >= 100) {
                Spacer(modifier = Modifier.width(1.dp))
                Text(
                    text = "+",
//                    color = calendarBadgeStyle().badgeBackgroundColor,
                    style = TextStyle(
                        fontSize = (animateItemHeight.value * 0.12).sp,
                        textAlign = TextAlign.Center,
//                        fontWeight = calendarBadgeStyle().badgeTextFontWeight,
                        lineHeight = TextUnit.Unspecified,
                        letterSpacing = 0.sp
                    ),
                    maxLines = 1
                )
            }
        }

    }
}
